import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import AFIPVerification from '../components/AFIPVerification';
import {
  Building2,
  Plus,
  Search,
  Eye,
  Edit2,
  Trash2,
  RefreshCw,
  Maximize2,
  FileText,
  CreditCard,
  Users,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Clock,
  UserPlus,
  Send,
  Copy,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';

const ESTADOS = [
  { value: 'ACTIVA', label: 'Activa', color: 'bg-green-100 text-green-700' },
  { value: 'INACTIVO', label: 'Inactivo', color: 'bg-slate-100 text-slate-700' },
  { value: 'BORRADOR', label: 'Borrador', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'EN_REVISION', label: 'En Revisión', color: 'bg-blue-100 text-blue-700' },
  { value: 'DOCUMENTACION_APROBADA', label: 'Doc. Aprobada', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'APROBADA', label: 'Aprobada', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'RECHAZADA', label: 'Rechazada', color: 'bg-red-100 text-red-700' },
  { value: 'INFO_ADICIONAL', label: 'Info Adicional', color: 'bg-orange-100 text-orange-700' },
  { value: 'PENDIENTE_APROBACION', label: 'Pend. Aprobación', color: 'bg-purple-100 text-purple-700' },
];

const EmpresasPage = () => {
  const navigate = useNavigate();
  
  // Lista de empresas
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  
  // Modal crear/editar empresa
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Modal de selección de tipo de creación
  const [showTypeSelectionDialog, setShowTypeSelectionDialog] = useState(false);
  
  // Modal de onboarding (por cliente)
  const [showOnboardingDialog, setShowOnboardingDialog] = useState(false);
  const [onboardingForm, setOnboardingForm] = useState({
    representante_cuit: '',
    representante_email: '',
    representante_nombre: '',
    empresa_nombre: '',
    padron_cliente_id: null,
    tipo_empresa: 'EMPRESA'  // EMPRESA o COOPERATIVA
  });
  const [onboardingResult, setOnboardingResult] = useState(null);
  const [verifyingCuit, setVerifyingCuit] = useState(false);
  const [padronInfo, setPadronInfo] = useState(null);
  const [afipVerificacion, setAfipVerificacion] = useState(null);
  
  // Modal crear desde cliente
  const [showFromClienteDialog, setShowFromClienteDialog] = useState(false);
  const [fromClienteCuit, setFromClienteCuit] = useState('');
  const [fromClienteCvu, setFromClienteCvu] = useState('');
  const [creatingFromCliente, setCreatingFromCliente] = useState(false);
  
  // Modal de vista rápida
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  
  const [formData, setFormData] = useState({
    razon_social: '',
    cuit: '',
    domicilio: '',
    localidad: '',
    provincia: '',
    codigo_postal: '',
    telefono: '',
    email: '',
    actividad_principal: '',
    notas: ''
  });

  useEffect(() => {
    loadEmpresas();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmpresas = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterEstado && filterEstado !== 'TODOS') params.append('estado', filterEstado);
      
      const res = await api.get(`/empresas?${params.toString()}`);
      setEmpresas(res.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(() => {
    loadEmpresas();
  }, [searchTerm, filterEstado]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(handleSearch, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filterEstado, handleSearch]);

  const getEstadoStyle = (estado) => {
    return ESTADOS.find(e => e.value === estado) || { value: estado, label: estado || 'Sin estado', color: 'bg-slate-100 text-slate-700' };
  };

  // ============== CRUD Empresa ==============

  const handleNewEmpresa = () => {
    // Mostrar modal de selección de tipo
    setShowTypeSelectionDialog(true);
  };

  const openCreateDialog = () => {
    setShowTypeSelectionDialog(false);
    setEditingEmpresa(null);
    setFormData({
      razon_social: '',
      cuit: '',
      domicilio: '',
      localidad: '',
      provincia: '',
      codigo_postal: '',
      telefono: '',
      email: '',
      actividad_principal: '',
      notas: ''
    });
    setShowCreateDialog(true);
  };

  const openOnboardingDialog = () => {
    setShowTypeSelectionDialog(false);
    setOnboardingForm({
      representante_cuit: '',
      representante_email: '',
      representante_nombre: '',
      empresa_nombre: '',
      padron_cliente_id: null
    });
    setOnboardingResult(null);
    setPadronInfo(null);
    setShowOnboardingDialog(true);
  };

  const handleCreateOnboarding = async () => {
    // Solo requeridos: Email y Nombre de Empresa
    if (!onboardingForm.representante_email || !onboardingForm.empresa_nombre) {
      toast.error('Complete los campos requeridos (Email y Nombre de Empresa)');
      return;
    }
    
    // Si se ingresó CUIT pero no existe en padrón, necesita nombre del representante
    if (onboardingForm.representante_cuit && !padronInfo?.exists && !onboardingForm.representante_nombre) {
      toast.error('Ingrese el nombre del representante legal');
      return;
    }
    
    // Si no hay CUIT, el nombre del representante es obligatorio
    if (!onboardingForm.representante_cuit && !onboardingForm.representante_nombre) {
      toast.error('Ingrese el nombre del representante legal');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post('/onboarding/create', {
        ...onboardingForm,
        representante_nombre: padronInfo?.cliente?.nombre 
          ? `${padronInfo.cliente.nombre} ${padronInfo.cliente.apellido || ''}`.trim()
          : onboardingForm.representante_nombre,
        // Si no hay CUIT, enviar vacío
        representante_cuit: onboardingForm.representante_cuit || null
      });
      setOnboardingResult(res.data);
      toast.success(
        '✅ Invitación enviada! Se envió un email al representante con las instrucciones para completar el onboarding.',
        { duration: 6000 }
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear solicitud');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const openEditDialog = (empresa) => {
    setEditingEmpresa(empresa);
    setFormData({
      razon_social: empresa.razon_social || '',
      cuit: empresa.cuit || '',
      domicilio: empresa.domicilio || '',
      localidad: empresa.localidad || '',
      provincia: empresa.provincia || '',
      codigo_postal: empresa.codigo_postal || '',
      telefono: empresa.telefono || '',
      email: empresa.email || '',
      actividad_principal: empresa.actividad_principal || '',
      notas: empresa.notas || ''
    });
    setShowCreateDialog(true);
  };

  const handleSaveEmpresa = async () => {
    if (!formData.razon_social.trim() || !formData.cuit.trim()) {
      toast.error('Razón Social y CUIT son requeridos');
      return;
    }

    setSaving(true);
    try {
      if (editingEmpresa) {
        await api.put(`/empresas/${editingEmpresa.id}`, formData);
        toast.success('Empresa actualizada');
        setShowCreateDialog(false);
        loadEmpresas();
      } else {
        const res = await api.post('/empresas/', formData);
        toast.success('Empresa creada');
        setShowCreateDialog(false);
        if (res.data && res.data.id) {
          navigate(`/m/empresas/empresa/${res.data.id}`);
        } else {
          loadEmpresas();
        }
      }
    } catch (error) {
      console.error('Error saving empresa:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEmpresa = async (empresa) => {
    if (!window.confirm(`¿Eliminar empresa "${empresa.razon_social}"?`)) return;
    
    try {
      await api.delete(`/empresas/${empresa.id}`);
      toast.success('Empresa eliminada');
      loadEmpresas();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Verificar CUIT en padrón
  const verifyCuitInPadron = async (cuit) => {
    if (!cuit || cuit.length < 8) {
      setPadronInfo(null);
      return;
    }
    
    // Limpiar CUIT (quitar guiones y espacios)
    const cleanCuit = cuit.replace(/[-\s]/g, '');
    
    setVerifyingCuit(true);
    try {
      const res = await api.get(`/padron/verify/cuit/${encodeURIComponent(cleanCuit)}`);
      setPadronInfo(res.data);
      
      if (res.data.exists && res.data.cliente) {
        const cliente = res.data.cliente;
        setOnboardingForm(prev => ({
          ...prev,
          padron_cliente_id: cliente.id,
          representante_nombre: `${cliente.nombre || ''} ${cliente.apellido || ''}`.trim(),
          // Si tiene email en el padrón, sugerirlo pero permitir cambiarlo
          representante_email: prev.representante_email || cliente.mail || ''
        }));
      } else {
        setOnboardingForm(prev => ({ ...prev, padron_cliente_id: null }));
      }
    } catch (error) {
      console.error('Error verificando CUIT:', error);
      setPadronInfo(null);
    } finally {
      setVerifyingCuit(false);
    }
  };

  const openDetail = (empresa) => {
    setSelectedEmpresa(empresa);
    setShowDetailDialog(true);
  };

  const goToFullDetail = (empresa) => {
    setShowDetailDialog(false);
    navigate(`/m/empresas/empresa/${empresa.id}`);
  };

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="text-purple-600" />
            Gestión de Empresas
          </h1>
          <p className="text-slate-600 mt-1">ABM de empresas con documentación y CVUs</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/m/empresas/crear-sucursal')}
            data-testid="crear-sucursal-nav-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium text-white"
            style={{ backgroundColor: '#0d9488' }}
          >
            <Building2 size={18} />
            Crear Sucursal
          </button>
          <button
            onClick={handleNewEmpresa}
            data-testid="new-empresa-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium text-white"
            style={{ 
              backgroundColor: '#7c3aed',
              minWidth: '160px'
            }}
          >
            <Plus size={18} />
            Nueva Empresa
          </button>
        </div>
      </div>

      {/* Filtros */}
      <Card className="p-4 bg-white mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por razón social o CUIT..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          <Select value={filterEstado} onValueChange={setFilterEstado}>
            <SelectTrigger className="w-48" style={{ backgroundColor: '#fff' }}>
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">Todos los estados</SelectItem>
              {ESTADOS.map(e => (
                <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Tabla de Empresas */}
      <Card className="bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Razón Social</TableHead>
              <TableHead>CUIT</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-center">Docs</TableHead>
              <TableHead className="text-center">CVUs</TableHead>
              <TableHead className="text-center">Cuentas</TableHead>
              <TableHead>Fecha</TableHead>
              <TableHead className="text-center">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                  Cargando...
                </TableCell>
              </TableRow>
            ) : empresas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  <Building2 size={40} className="mx-auto mb-2 text-slate-300" />
                  No hay empresas registradas
                </TableCell>
              </TableRow>
            ) : (
              empresas.map(empresa => {
                const estadoStyle = getEstadoStyle(empresa.estado);
                
                return (
                  <TableRow 
                    key={empresa.id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => openDetail(empresa)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-slate-900">{empresa.razon_social}</div>
                        {empresa.tipo_empresa === 'COOPERATIVA' && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5">
                            Coop
                          </Badge>
                        )}
                      </div>
                      {empresa.actividad_principal && (
                        <div className="text-xs text-slate-500">{empresa.actividad_principal}</div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{empresa.cuit}</TableCell>
                    <TableCell>
                      <Badge className={estadoStyle.color}>{estadoStyle.label}</Badge>
                      {empresa.estado === 'PENDIENTE_APROBACION' && (
                        <span className="ml-1 inline-flex items-center" title="Esperando aprobación">
                          <Clock size={14} className="text-purple-600 animate-pulse" />
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm">
                        {empresa.documentos_aprobados}/{empresa.total_documentos}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm">{empresa.total_cvus}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm">{empresa.total_cuentas}</span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(empresa.fecha_creacion).toLocaleDateString()}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDetail(empresa)}
                          data-testid={`view-empresa-${empresa.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(empresa)}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEmpresa(empresa)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Dialog: Crear/Editar Empresa */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="text-purple-600" />
              {editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Razón Social *</Label>
              <Input
                value={formData.razon_social}
                onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                placeholder="Nombre de la empresa"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div>
              <Label>CUIT *</Label>
              <Input
                value={formData.cuit}
                onChange={(e) => setFormData({ ...formData, cuit: e.target.value })}
                placeholder="XX-XXXXXXXX-X"
                disabled={!!editingEmpresa}
                style={{ backgroundColor: editingEmpresa ? '#f1f5f9' : '#fff' }}
              />
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contacto@empresa.com"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div>
              <Label>Teléfono</Label>
              <Input
                value={formData.telefono}
                onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                placeholder="+54 11 XXXX-XXXX"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div>
              <Label>Actividad Principal</Label>
              <Input
                value={formData.actividad_principal}
                onChange={(e) => setFormData({ ...formData, actividad_principal: e.target.value })}
                placeholder="Ej: Comercio mayorista"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div className="col-span-2">
              <Label>Domicilio</Label>
              <Input
                value={formData.domicilio}
                onChange={(e) => setFormData({ ...formData, domicilio: e.target.value })}
                placeholder="Dirección completa"
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div>
              <Label>Localidad</Label>
              <Input
                value={formData.localidad}
                onChange={(e) => setFormData({ ...formData, localidad: e.target.value })}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div>
              <Label>Provincia</Label>
              <Input
                value={formData.provincia}
                onChange={(e) => setFormData({ ...formData, provincia: e.target.value })}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
            
            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                rows={2}
                style={{ backgroundColor: '#fff' }}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <button
              onClick={handleSaveEmpresa}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: '#7c3aed' }}
            >
              {saving ? 'Guardando...' : (editingEmpresa ? 'Actualizar' : 'Crear Empresa')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Vista Rápida de Empresa */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
          {selectedEmpresa && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="text-purple-600" />
                  Vista Rápida de Empresa
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Estado */}
                <div className="flex flex-wrap gap-2 items-center">
                  <Badge className={getEstadoStyle(selectedEmpresa.estado).color}>
                    {getEstadoStyle(selectedEmpresa.estado).label}
                  </Badge>
                  {selectedEmpresa.documentos_aprobados < selectedEmpresa.total_documentos && (
                    <Badge className="bg-amber-100 text-amber-800">
                      <FileText size={12} className="mr-1" />
                      Docs pendientes: {selectedEmpresa.total_documentos - selectedEmpresa.documentos_aprobados}
                    </Badge>
                  )}
                </div>

                {/* Nombre */}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-slate-900">
                      {selectedEmpresa.razon_social}
                    </h3>
                    {selectedEmpresa.tipo_empresa === 'COOPERATIVA' && (
                      <Badge className="bg-purple-100 text-purple-700">Cooperativa</Badge>
                    )}
                  </div>
                  {selectedEmpresa.actividad_principal && (
                    <p className="text-sm text-slate-500">{selectedEmpresa.actividad_principal}</p>
                  )}
                </div>

                {/* Información clave */}
                <Card className="p-4 bg-slate-50">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <Label className="text-xs text-slate-500">CUIT</Label>
                      <p className="font-mono text-slate-900">{selectedEmpresa.cuit}</p>
                    </div>
                    {selectedEmpresa.email && (
                      <div>
                        <Label className="text-xs text-slate-500">Email</Label>
                        <p className="text-slate-900 flex items-center gap-1">
                          <Mail size={14} className="text-slate-400" />
                          {selectedEmpresa.email}
                        </p>
                      </div>
                    )}
                    {selectedEmpresa.telefono && (
                      <div>
                        <Label className="text-xs text-slate-500">Teléfono</Label>
                        <p className="text-slate-900 flex items-center gap-1">
                          <Phone size={14} className="text-slate-400" />
                          {selectedEmpresa.telefono}
                        </p>
                      </div>
                    )}
                    {selectedEmpresa.domicilio && (
                      <div className="col-span-2">
                        <Label className="text-xs text-slate-500">Domicilio</Label>
                        <p className="text-slate-900 flex items-center gap-1">
                          <MapPin size={14} className="text-slate-400" />
                          {selectedEmpresa.domicilio}
                          {selectedEmpresa.localidad && `, ${selectedEmpresa.localidad}`}
                          {selectedEmpresa.provincia && `, ${selectedEmpresa.provincia}`}
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-xs text-slate-500">Fecha de Creación</Label>
                      <p className="text-slate-900 flex items-center gap-1">
                        <Calendar size={14} className="text-slate-400" />
                        {new Date(selectedEmpresa.fecha_creacion).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Estadísticas */}
                <div className="grid grid-cols-3 gap-3">
                  <Card className="p-3 text-center bg-blue-50 border-blue-200">
                    <FileText size={20} className="mx-auto text-blue-600 mb-1" />
                    <p className="text-lg font-bold text-blue-900">
                      {selectedEmpresa.documentos_aprobados}/{selectedEmpresa.total_documentos}
                    </p>
                    <p className="text-xs text-blue-600">Documentos</p>
                  </Card>
                  <Card className="p-3 text-center bg-green-50 border-green-200">
                    <CreditCard size={20} className="mx-auto text-green-600 mb-1" />
                    <p className="text-lg font-bold text-green-900">{selectedEmpresa.total_cvus}</p>
                    <p className="text-xs text-green-600">CVUs</p>
                  </Card>
                  <Card className="p-3 text-center bg-purple-50 border-purple-200">
                    <Users size={20} className="mx-auto text-purple-600 mb-1" />
                    <p className="text-lg font-bold text-purple-900">{selectedEmpresa.total_cuentas}</p>
                    <p className="text-xs text-purple-600">Cuentas</p>
                  </Card>
                </div>

                {/* Notas */}
                {selectedEmpresa.notas && (
                  <Card className="p-3 bg-amber-50 border-amber-200">
                    <Label className="text-xs text-amber-700 mb-1">Notas</Label>
                    <p className="text-sm text-amber-900">{selectedEmpresa.notas}</p>
                  </Card>
                )}

                {/* Botón para ir a pantalla completa */}
                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setShowDetailDialog(false); openEditDialog(selectedEmpresa); }}
                    >
                      <Edit2 size={14} className="mr-1" />
                      Editar
                    </Button>
                  </div>
                  <Button
                    onClick={() => goToFullDetail(selectedEmpresa)}
                    style={{ backgroundColor: '#7c3aed' }}
                    data-testid="goto-empresa-detail-btn"
                  >
                    <Maximize2 size={14} className="mr-2" />
                    Ver Pantalla Completa
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Selección de Tipo de Creación */}
      <Dialog open={showTypeSelectionDialog} onOpenChange={setShowTypeSelectionDialog}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="text-purple-600" />
              Nueva Empresa
            </DialogTitle>
            <DialogDescription>
              ¿Cómo desea crear la empresa?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Opción 1: Crear nosotros */}
            <button
              onClick={openCreateDialog}
              className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-colors text-left"
              data-testid="create-by-us-btn"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-slate-600" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Crear nosotros</p>
                  <p className="text-sm text-slate-500">
                    Complete el formulario con los datos de la empresa y documentación
                  </p>
                </div>
              </div>
            </button>
            
            {/* Opción 2: Por el cliente */}
            <button
              onClick={openOnboardingDialog}
              className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-colors text-left"
              data-testid="create-by-client-btn"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="text-slate-600" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">Por el cliente</p>
                  <p className="text-sm text-slate-500">
                    El representante legal recibirá un enlace para completar los datos
                  </p>
                </div>
              </div>
            </button>
            
            {/* Opción 3: Desde cliente existente */}
            <button
              onClick={() => { setShowTypeSelectionDialog(false); setShowFromClienteDialog(true); }}
              className="w-full p-4 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left"
              data-testid="create-from-cliente-btn"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Users className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="font-semibold text-green-800">Desde cliente existente</p>
                  <p className="text-sm text-green-600">
                    Crear empresa automáticamente desde los datos de un cliente del padrón
                  </p>
                </div>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Onboarding (Por Cliente) */}
      <Dialog open={showOnboardingDialog} onOpenChange={setShowOnboardingDialog}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="text-blue-600" />
              Alta de Empresa por Cliente
            </DialogTitle>
            <DialogDescription>
              Ingrese los datos del representante legal. Se validará contra el padrón de clientes.
            </DialogDescription>
          </DialogHeader>
          
          {!onboardingResult ? (
            <>
              <div className="space-y-4 py-4">
                {/* Tipo de Empresa */}
                <div>
                  <Label>Tipo de Entidad *</Label>
                  <div className="flex gap-4 mt-2">
                    <label className={`flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-all ${
                      onboardingForm.tipo_empresa === 'EMPRESA' 
                        ? 'border-blue-500 bg-blue-50 text-blue-700' 
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                      <input
                        type="radio"
                        name="tipo_empresa"
                        value="EMPRESA"
                        checked={onboardingForm.tipo_empresa === 'EMPRESA'}
                        onChange={(e) => setOnboardingForm({ ...onboardingForm, tipo_empresa: e.target.value })}
                        className="sr-only"
                      />
                      <Building2 size={20} />
                      <span className="font-medium">Empresa</span>
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-all ${
                      onboardingForm.tipo_empresa === 'COOPERATIVA' 
                        ? 'border-purple-500 bg-purple-50 text-purple-700' 
                        : 'border-slate-200 hover:bg-slate-50'
                    }`}>
                      <input
                        type="radio"
                        name="tipo_empresa"
                        value="COOPERATIVA"
                        checked={onboardingForm.tipo_empresa === 'COOPERATIVA'}
                        onChange={(e) => setOnboardingForm({ ...onboardingForm, tipo_empresa: e.target.value })}
                        className="sr-only"
                      />
                      <Users size={20} />
                      <span className="font-medium">Cooperativa</span>
                    </label>
                  </div>
                  {onboardingForm.tipo_empresa === 'COOPERATIVA' && (
                    <p className="text-xs text-purple-600 mt-2 p-2 bg-purple-50 rounded">
                      Se solicitará documentación específica para cooperativas: Acta Constitutiva, Estatuto, Matrícula INAES, Acta de Autoridades, Balances y DNI de firmantes.
                    </p>
                  )}
                </div>
                
                <div>
                  <Label>Nombre de la {onboardingForm.tipo_empresa === 'COOPERATIVA' ? 'Cooperativa' : 'Empresa'} *</Label>
                  <Input
                    placeholder={onboardingForm.tipo_empresa === 'COOPERATIVA' ? 'Nombre de la cooperativa' : 'Razón social o nombre comercial'}
                    value={onboardingForm.empresa_nombre}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, empresa_nombre: e.target.value })}
                    data-testid="onboarding-empresa-nombre"
                    style={{ backgroundColor: '#fff' }}
                  />
                </div>
                
                <div>
                  <Label>CUIT del Representante Legal (opcional)</Label>
                  <Input
                    placeholder="XX-XXXXXXXX-X"
                    value={onboardingForm.representante_cuit}
                    onChange={(e) => {
                      setOnboardingForm({ ...onboardingForm, representante_cuit: e.target.value });
                    }}
                    onBlur={(e) => e.target.value && verifyCuitInPadron(e.target.value)}
                    data-testid="onboarding-representante-cuit"
                    style={{ backgroundColor: '#fff' }}
                  />
                  <p className="text-xs text-slate-500 mt-1">Si tiene CUIT, ingreselo para vincular automáticamente con el padrón</p>
                  {verifyingCuit && (
                    <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                      <RefreshCw size={12} className="animate-spin" />
                      Verificando CUIT en padrón...
                    </p>
                  )}
                  {!verifyingCuit && padronInfo && (
                    padronInfo.exists ? (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <p className="text-green-700 font-medium flex items-center gap-1 mb-2">
                          <CheckCircle size={14} />
                          Representante encontrado en padrón
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-green-600">
                          <div>
                            <span className="text-xs text-green-500">Nombre:</span>
                            <p className="font-medium">{padronInfo.cliente.nombre} {padronInfo.cliente.apellido}</p>
                          </div>
                          <div>
                            <span className="text-xs text-green-500">CUIT:</span>
                            <p className="font-medium">{padronInfo.cliente.cuit}</p>
                          </div>
                          {padronInfo.cliente.mail && (
                            <div className="col-span-2">
                              <span className="text-xs text-green-500">Email en padrón:</span>
                              <p className="font-medium">{padronInfo.cliente.mail}</p>
                            </div>
                          )}
                          {padronInfo.cliente.razon_social && (
                            <div className="col-span-2">
                              <span className="text-xs text-green-500">Razón Social:</span>
                              <p className="font-medium">{padronInfo.cliente.razon_social}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                        <p className="text-amber-700 flex items-center gap-1">
                          <AlertTriangle size={14} />
                          CUIT no encontrado en padrón. Puede continuar sin vincular.
                        </p>
                        <p className="text-amber-600 mt-1">
                          El cliente podrá ser vinculado más adelante desde la solicitud.
                        </p>
                      </div>
                    )
                  )}
                  
                  {/* Verificación AFIP */}
                  {padronInfo?.exists && (
                    <div className="mt-3">
                      <AFIPVerification 
                        cuit={onboardingForm.representante_cuit}
                        variant="card"
                        showDetails={true}
                        onVerified={(data) => setAfipVerificacion(data)}
                      />
                    </div>
                  )}
                </div>
                
                {/* Nombre del representante - siempre visible si no hay CUIT o si CUIT no está en padrón */}
                {(!onboardingForm.representante_cuit || !padronInfo?.exists) && (
                  <div>
                    <Label>Nombre del Representante Legal *</Label>
                    <Input
                      placeholder="Nombre y apellido"
                      value={onboardingForm.representante_nombre}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, representante_nombre: e.target.value })}
                      data-testid="onboarding-representante-nombre"
                      style={{ backgroundColor: '#fff' }}
                    />
                  </div>
                )}
                
                <div>
                  <Label>Email del Representante Legal *</Label>
                  <Input
                    type="email"
                    placeholder="email@ejemplo.com"
                    value={onboardingForm.representante_email}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, representante_email: e.target.value })}
                    data-testid="onboarding-representante-email"
                    style={{ backgroundColor: '#fff' }}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Se enviará un email con el enlace y credenciales temporales a esta dirección
                  </p>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowOnboardingDialog(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCreateOnboarding} 
                  disabled={saving || !onboardingForm.empresa_nombre || !onboardingForm.representante_email || (!onboardingForm.representante_cuit && !onboardingForm.representante_nombre)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="send-onboarding-btn"
                >
                  <Send size={16} className="mr-2" />
                  {saving ? 'Enviando...' : 'Enviar Invitación'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-lg">
                    Invitación Enviada
                  </h3>
                  <p className="text-slate-500 mt-1">
                    Se envió un email a <strong>{onboardingResult.representante_email}</strong>
                  </p>
                </div>
                
                <Card className="p-4 bg-slate-50">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Empresa</Label>
                      <p className="font-medium">{onboardingResult.empresa_nombre_inicial}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Representante</Label>
                      <p className="font-medium">{onboardingResult.representante_nombre}</p>
                    </div>
                    {onboardingResult._temp_password && (
                      <div>
                        <Label className="text-xs text-slate-500">Contraseña Temporal</Label>
                        <div className="flex items-center gap-2">
                          <code className="font-mono text-sm bg-slate-200 px-2 py-1 rounded">
                            {onboardingResult._temp_password}
                          </code>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(onboardingResult._temp_password)}
                          >
                            <Copy size={14} />
                          </Button>
                        </div>
                        <p className="text-xs text-amber-600 mt-1">
                          Esta contraseña también fue enviada en el email
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
                
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Próximos pasos:</strong> El representante recibirá un email con las instrucciones 
                    para completar los datos de la empresa, subir documentación y definir límites operativos.
                  </p>
                </div>
              </div>
              
              <DialogFooter>
                <Button onClick={() => setShowOnboardingDialog(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Crear Empresa desde Cliente */}
      <Dialog open={showFromClienteDialog} onOpenChange={setShowFromClienteDialog}>
        <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-green-600" />
              Crear Empresa desde Cliente
            </DialogTitle>
            <DialogDescription>
              Ingrese el CUIT del cliente existente para crear una empresa con sus datos automáticamente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>CUIT del Cliente *</Label>
              <Input
                placeholder="XX-XXXXXXXX-X o XXXXXXXXXXX"
                value={fromClienteCuit}
                onChange={(e) => setFromClienteCuit(e.target.value)}
                data-testid="from-cliente-cuit"
                style={{ backgroundColor: '#fff' }}
              />
              <p className="text-xs text-slate-500 mt-1">
                Debe ser un cliente existente en el padrón
              </p>
            </div>
            
            <div>
              <Label>CVU (opcional)</Label>
              <Input
                placeholder="CVU del cliente"
                value={fromClienteCvu}
                onChange={(e) => setFromClienteCvu(e.target.value)}
                data-testid="from-cliente-cvu"
                style={{ backgroundColor: '#fff' }}
              />
              <p className="text-xs text-slate-500 mt-1">
                Si se proporciona, se asociará a la empresa
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFromClienteDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (!fromClienteCuit) {
                  toast.error('Ingrese el CUIT del cliente');
                  return;
                }
                setCreatingFromCliente(true);
                try {
                  const res = await api.post('/empresas/desde-cliente', {
                    cliente_cuit: fromClienteCuit.replace(/-/g, ''),
                    cliente_cvu: fromClienteCvu || null
                  });
                  toast.success('Empresa creada correctamente');
                  setShowFromClienteDialog(false);
                  setFromClienteCuit('');
                  setFromClienteCvu('');
                  loadEmpresas();
                  // Navegar al detalle de la nueva empresa
                  if (res.data.empresa?.id) {
                    navigate(`/m/clientes/empresas/${res.data.empresa.id}`);
                  }
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Error al crear empresa');
                } finally {
                  setCreatingFromCliente(false);
                }
              }}
              disabled={creatingFromCliente || !fromClienteCuit}
              style={{ backgroundColor: '#059669' }}
              data-testid="confirm-create-from-cliente-btn"
            >
              {creatingFromCliente ? (
                <>
                  <RefreshCw size={14} className="mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus size={14} className="mr-2" />
                  Crear Empresa
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpresasPage;
